import { useMemo, useState } from "react";
import type { Program, Product } from "@/lib/types";

// ─── COMPLETION STAGES ────────────────────────────────────────────
const PIPELINE_STAGES = [
  { key: "info", label: "Product Info" },
  { key: "media", label: "Media" },
  { key: "copy", label: "Copy" },
  { key: "vd", label: "Visual Direction" },
  { key: "images", label: "Image Gen" },
] as const;

type StageKey = (typeof PIPELINE_STAGES)[number]["key"];

// ─── PER-PRODUCT COMPLETION CHECKS ───────────────────────────────
function isInfoComplete(p: Product): boolean {
  const hasFeatures = (p.sellingFeatures || []).length >= 3;
  const hasCategory = !!p.category;
  const hasDimensions = !!p.dimensions;
  const hasMaterial = !!p.material;
  const hasAudience = !!p.targetAudience;
  const hasUseCases = Array.isArray(p.useCases) ? p.useCases.length > 0 : !!p.useCases;
  return hasFeatures && hasCategory && hasDimensions && hasMaterial && hasAudience && hasUseCases;
}

function isMediaComplete(p: Product): boolean {
  return !!p.heroImage;
}

function isCopyComplete(p: Product): boolean {
  return !!p.approvedCopy && Object.keys(p.approvedCopy).length > 0;
}

function isVDComplete(p: Product): boolean {
  return !!p.artDirection?.images && p.artDirection.images.length > 0;
}

function isImagesComplete(p: Product): boolean {
  const genImages = p.artDirection?.generatedImages;
  if (!genImages) return false;
  // Has at least one style with at least one generated image
  return Object.values(genImages).some(
    (styleImages: any) => styleImages && typeof styleImages === "object" && Object.keys(styleImages).length > 0
  );
}

const stageChecks: Record<StageKey, (p: Product) => boolean> = {
  info: isInfoComplete,
  media: isMediaComplete,
  copy: isCopyComplete,
  vd: isVDComplete,
  images: isImagesComplete,
};

// ─── COMPUTE PROGRAM COMPLETION ──────────────────────────────────
interface ProgramCompletion {
  program: Program;
  productCount: number;
  stages: Record<StageKey, { done: number; total: number }>;
  overallPercent: number;
}

function computeProgramCompletion(program: Program, products: Product[]): ProgramCompletion {
  const programProducts = products.filter(p => String(p.programId) === String(program.id));
  const total = programProducts.length;

  const stages = {} as Record<StageKey, { done: number; total: number }>;
  let totalDone = 0;

  for (const stage of PIPELINE_STAGES) {
    const done = total > 0 ? programProducts.filter(p => stageChecks[stage.key](p)).length : 0;
    stages[stage.key] = { done, total };
    totalDone += done;
  }

  const overallPercent = total > 0 ? Math.round((totalDone / (total * PIPELINE_STAGES.length)) * 100) : 0;

  return { program, productCount: total, stages, overallPercent };
}

// ─── PROPS ────────────────────────────────────────────────────────
interface PipelinePageProps {
  programs?: Program[];
  products?: Product[];
  onOpenProgram?: (programId: string | number | undefined) => void;
}

export function PipelinePage({
  programs = [],
  products = [],
  onOpenProgram = () => {},
}: PipelinePageProps) {
  const [hoveredId, setHoveredId] = useState<string | number | null>(null);

  const completions = useMemo(() => {
    const list = programs.map(prog => computeProgramCompletion(prog, products));
    list.sort((a, b) => a.overallPercent - b.overallPercent);
    return list;
  }, [programs, products]);

  return (
    <>
      <div className="mb-10">
        <h1 className="page-title">Pipeline</h1>
        <p className="page-subtitle">
          Program completion across the content workflow
        </p>
      </div>

      {/* Column Headers */}
      <div className="flex items-center gap-3 mb-2 pad-header-sm">
        {/* Program name column */}
        <div className="col-fixed-180">
          <span className="ff-primary fw-700 uppercase ls-wide pipeline-col-label">Program</span>
        </div>

        {/* Product count spacer (no header — number is self-explanatory) */}
        <div className="col-44 min-w-44" />

        {/* Stage columns */}
        <div className="flex flex-1 gap-2">
          {PIPELINE_STAGES.map(s => (
            <div key={s.key} className="flex-1 text-center">
              <span className="ff-primary fw-700 uppercase ls-wide pipeline-col-label">{s.label}</span>
            </div>
          ))}
        </div>

        {/* Overall badge spacer */}
        <div className="col-fixed-60" />
      </div>

      {/* Program Rows */}
      <div className="flex-col gap-2">
        {completions.map(c => {
          const isHovered = hoveredId === c.program.id;

          return (
            <div
              key={c.program.id}
              onClick={() => onOpenProgram(c.program.id)}
              onMouseEnter={() => setHoveredId(c.program.id ?? null)}
              onMouseLeave={() => setHoveredId(null)}
              className={`flex items-center gap-3 bg-card rounded pointer pad-row program-row-card ${isHovered ? 'border-slate-focus' : 'border'}`}
            >
              {/* Program name */}
              <div className="col-fixed-180">
                <div className="fw-600 ff-secondary text-dark text-lg truncate">{c.program.name}</div>
                {c.program.channel && (
                  <div className="text-light ff-secondary text-md mt-1">{c.program.channel}</div>
                )}
              </div>

              {/* Product count */}
              <div className="fw-600 ff-secondary text-light text-base col-44 min-w-44 text-center">
                {c.productCount}
              </div>

              {/* Stage checks */}
              <div className="flex flex-1 gap-2 items-center">
                {PIPELINE_STAGES.map(s => {
                  const stage = c.stages[s.key];
                  const pct = stage.total > 0 ? Math.round((stage.done / stage.total) * 100) : 0;
                  const isReady = pct >= 80;

                  return (
                    <div key={s.key} className="readiness-check readiness-col" title={`${s.label} ${pct}%`}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={isReady ? 'var(--peach)' : 'var(--border)'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      <span className={`ff-secondary fw-600 text-sm ${isReady ? 'text-peach' : 'text-light'}`}>
                        {stage.done}/{stage.total}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Overall % */}
              <div className="readiness-check col-fixed-60 justify-end" title={`Overall ${c.overallPercent}%`}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={c.overallPercent >= 80 ? 'var(--peach)' : 'var(--border)'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span className={`ff-secondary fw-600 text-sm ${c.overallPercent >= 80 ? 'text-peach' : 'text-light'}`}>
                  {c.overallPercent >= 80 ? 'Ready' : `${c.overallPercent}%`}
                </span>
              </div>
            </div>
          );
        })}

        {completions.length === 0 && (
          <div className="empty-state-styled">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--peach)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mb-3">
              <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
            <div className="text-md ff-secondary fw-600 text-mid mb-2">No programs yet</div>
            <div className="text-sm ff-secondary text-light">Create a program to start organizing your product pipeline</div>
          </div>
        )}
      </div>
    </>
  );
}
